import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';

import { api } from '../api.js';
import { refreshNavCounts } from '../hooks/useNavCounts.js';
import { toast } from './Toasts.jsx';

// T361 — stage changes the mail suggests, waiting for his word.
//
// The operator asked for exactly this: decisions taken from the mail, the mail
// text kept so he can read it, and his approval before anything moves. So
// nothing here moves on its own, and every card shows the message it is
// reasoning from — the evidence sits next to the question, not behind a link.
//
// Approving goes through the ordinary recommendations PATCH, the one place that
// knows how to record a transition, snapshot an outcome, invalidate a
// disposition and infer respondedAt. A second copy of that logic would drift
// from it the first time either changed.

const STAGE_LABEL = { applied: 'Applied', screening: 'Screening', interview: 'Interview', offer: 'Offer', rejected: 'Rejected' };

function fmt(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function Card({ p, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [showMail, setShowMail] = useState(false);

  const resolve = async (approve) => {
    setBusy(true);
    try {
      if (approve) {
        // The canonical path. Not a private copy of it.
        await api.patch(`/recommendations/${p.recommendationId}`, { status: p.to });
      }
      await api.patch(`/emails/${p.emailId}`, { proposalResolved: true });
      onResolved(p.emailId);
      // T387 — the sidebar badge polls on its own clock, so without this it
      // keeps advertising work he has just finished for up to a minute. A
      // badge whose whole purpose is to reach zero teaches him it lies.
      // `refreshNavCounts` is the existing hook-free path for exactly this.
      refreshNavCounts();
      toast(approve ? `Moved to ${STAGE_LABEL[p.to] ?? p.to}.` : 'Left as it is.', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || 'That did not go through.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const isReject = p.to === 'rejected';

  return (
    <li className={`rounded-md border px-3 py-3 ${isReject ? 'border-amber-800 bg-amber-950/20' : 'border-slate-800 bg-slate-900/40'}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm font-medium text-slate-200">{p.company || 'Unknown company'}</span>
        {p.title && <span className="truncate text-xs text-slate-500">{p.title}</span>}
      </div>

      <p className="mt-1.5 text-xs text-slate-300">
        <span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[11px] text-slate-400">
          {STAGE_LABEL[p.from] ?? p.from}
        </span>
        <span aria-hidden="true" className="mx-1.5 text-slate-500">→</span>
        <span className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${isReject ? 'bg-amber-600/20 text-amber-300' : 'bg-brand-600/20 text-brand-400'}`}>
          {STAGE_LABEL[p.to] ?? p.to}
        </span>
        <span className="ml-2 text-slate-500">
          — {p.reason}
          {/* Said out loud: a subject line is a stronger claim than a phrase
              found somewhere in the body, and he should weigh them differently. */}
          {p.evidence === 'body' && <span className="text-slate-600"> (from the message body, worth a read)</span>}
          {/* T367 — where the reading came from matters to how hard he should
              look before approving. A rule matched a phrase; Claude formed a
              judgement, and the sentence it judged on is shown. */}
          {p.evidence === 'ai' && <span className="text-slate-600"> (read by Claude, worth a read)</span>}
          {/* T483 — this one is a question, not a reading. The employer said
              the application was STARTED and is waiting on a confirmation
              click, so only he knows whether he finished it. */}
          {p.evidence === 'started' && (
            <span className="text-slate-600"> (you started this one — did you finish it?)</span>
          )}
        </span>
      </p>

      {p.aiEvidence && (
        <p className="mt-1 border-l-2 border-slate-700 pl-2 text-[11px] italic text-slate-400">
          &ldquo;{p.aiEvidence}&rdquo;
        </p>
      )}

      <button
        type="button"
        onClick={() => setShowMail((v) => !v)}
        aria-expanded={showMail}
        className="mt-2 block w-full truncate text-left text-xs text-slate-400 underline decoration-dotted transition hover:text-slate-200"
      >
        {showMail ? '▾' : '▸'} {p.subject || '(no subject)'} · {fmt(p.sentAt)}
      </button>

      {showMail && (
        <pre className="mt-1.5 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded border border-slate-800 bg-slate-950/50 p-2 font-sans text-[11px] leading-relaxed text-slate-300">
          {p.bodyText || '(empty)'}
        </pre>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => resolve(true)}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
        >
          Move to {STAGE_LABEL[p.to] ?? p.to}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => resolve(false)}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition hover:border-slate-500 hover:text-slate-100 disabled:opacity-50"
        >
          No, leave it
        </button>
      </div>
    </li>
  );
}

// T387 — the page that OWNS the fetch button has to be able to reload this.
//
// `load` ran once on mount and nothing could ask it to run again. So a scan
// could finish, the toast could say "2 stage changes to approve", and this
// section would keep showing whatever it fetched when the page opened —
// which, for the operator, was nothing at all. The interview beside it
// repainted (TasksPage refetches its own calendar), so the page looked alive
// and looked like it was answering him.
const StageProposals = forwardRef(function StageProposals({ onCount, showEmpty = true }, ref) {
  const [proposals, setProposals] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/emails/proposals');
      setProposals(r.proposals ?? []);
      setFailed(false);
      onCount?.(r.proposals?.length ?? 0);
    } catch {
      setProposals([]);
      // T387 — "could not load" and "there is nothing" were the same blank
      // space. Told there were two, and shown an empty page, he had no way to
      // tell a failed request from an honest zero.
      setFailed(true);
    }
  }, [onCount]);

  useImperativeHandle(ref, () => ({ reload: load }), [load]);

  useEffect(() => {
    load();
  }, [load]);

  const drop = (emailId) =>
    setProposals((prev) => {
      const next = (prev ?? []).filter((p) => p.emailId !== emailId);
      onCount?.(next.length);
      return next;
    });

  // Still loading: nothing yet, deliberately — a skeleton here would flash on
  // every page open for a section that is usually empty.
  if (!proposals) return null;

  if (proposals.length === 0) {
    // The page owns the whole-page-empty case; two "nothing waiting" lines on
    // one screen is worse than none.
    if (!showEmpty) return null;
    // T387 — say something. An empty render is indistinguishable from a
    // section that never ran, and the toast may have just promised him work.
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-brand-500">Suggested stage changes</h2>
        <p className="max-w-prose text-xs text-slate-500">
          {failed
            ? 'Could not load the suggestions just now — try Fetch mail again.'
            : 'Nothing waiting. Replies that suggest a stage change will appear here.'}
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-medium text-brand-500">Suggested stage changes</h2>
      <p className="max-w-prose text-xs text-slate-500">
        Read from the replies below. Nothing moves until you say so, and saying no just clears the
        suggestion — it does not change the application.
      </p>
      <ul className="space-y-2">
        {proposals.map((p) => (
          <Card key={p.emailId} p={p} onResolved={drop} />
        ))}
      </ul>
    </section>
  );
});

export default StageProposals;
